import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import {
    Box, Button, Divider, Grid, IconButton,
    Paper, TextField, Typography,
} from "@mui/material";
import { Add, Delete, InsertDriveFile, UploadFile } from "@mui/icons-material";
import AppForm from "components/Shared/AppForm";
import AppSwitch from "components/Shared/AppSwitch";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { useAuth } from "hooks/useAuth";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useDOUploadFile } from "hooks";
import defaultLogoSrc from "assets/TMS-NepalCan-Logo-Quotation.png";
import { generateVendorQuotationExcel } from "utils/generateVendorQuotationExcel";
import { parseVendorQuotationExcel } from "./parseVendorQuotationExcel";
import { toast } from "react-toastify";

// ─── Default State ─────────────────────────────────────────────────────────────

const INITIAL_STATE = {
    account: null,
    entity: null,
    title: "",
    isActive: true,

    headerCompanyName: "Nepal Can Logistics Pvt. Ltd.",
    headerFormerName: "(Formerly known as Nepal Can Transport Pvt. Ltd.)",
    headerRegnNo: "Regd no. 306615/079/080",
    headerPanNo: "PAN no. 610442580",

    date: "",
    toName: "",
    toAddress: "",
    subject: "Service Quotation",

    termsAndConditions: [
        "Quoted rates Excluding 13% VAT.",
        "The Consignor is solely responsible for insuring the consignment to cover all risks of loss or damage, as the transport company will not be liable for such claims.",
        "The Consignor must ensure full compliance with all government/customs rules. The transport company is not responsible for goods declared contraband or subsequently confiscated by the Government.",
        "This quotation is valid for a period of 7 days only",
        "Rates remain stable within a Rs±10 fluctuation in fuel prices; any variation beyond this range may lead to revision upon mutual agreement.",
    ],

    closingNote: "Please feel free to write to us if you have any further queries about it.",
    thankYouNote: "Thank You.",
    regards: "With Regards,",
    signatoryName: "",
    signatoryDesignation: "",
    signatoryCompany: "Nepal Can Logistics Pvt. Ltd.",
    signatoryAddress: "Jadibuti, Kathmandu, Nepal",
    signatoryPhone: "",
    signatoryEmail: "",

    footerLeft: "Muni Bhairab Marga, Tinkune,\nKMC-32, Kathmandu",
    footerCenter: "01-5970736",
    footerRightWeb: "www.logistics.nepalcan.com",
    footerRightEmail: "care@logistics.nepalcan.com",
};

// ─── Sub-components ────────────────────────────────────────────────────────────

const SectionHeading = ({ children }) => (
    <Grid item xs={12} sx={{ mt: 1 }}>
        <Divider sx={{ mb: 1.5 }} />
        <Typography variant="subtitle1" fontWeight={700} color="primary">
            {children}
        </Typography>
    </Grid>
);

// ─── Main Form ─────────────────────────────────────────────────────────────────

const VendorQuotationExcelImportForm = ({
    isBusy = false,
    priceListDetail = null,
    onSubmit = () => { },
}) => {
    const navigate = useNavigate();
    const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();
    const logoInputRef = useRef(null);
    const excelInputRef = useRef(null);
    const isMounted = useRef(true);

    const { handleUploadFiles, uploadingFiles } = useDOUploadFile();
    const [logoPreview, setLogoPreview] = useState(defaultLogoSrc);

    // Three-state Excel file tracking:
    // pendingFile   → new File selected this session, not yet uploaded
    // existingFile  → attachment object already saved in DB
    // removedExisting → user explicitly cleared the saved one
    const [pendingFile, setPendingFile] = useState(null);
    const [existingFile, setExistingFile] = useState(null);
    const [removedExisting, setRemovedExisting] = useState(false);

    const { data: accounts, isLoading: loadingAccounts } = useGetDDLAccountListQuery();
    const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
        useLazyGetDDLEntityListQuery();

    const {
        control,
        handleSubmit,
        setValue,
        reset,
        watch,
        formState: { errors },
    } = useForm({
        defaultValues: {
            ...INITIAL_STATE,
            entity: viewEntity?.entity || null,
            account: viewEntity?.entity?.account || null,
        },
        mode: "onChange",
    });

    const _watchAccount = useWatch({ control, name: "account" });
    const termsAndConditions = watch("termsAndConditions");

    // ── Populate on edit ──
    useEffect(() => {
        if (!priceListDetail) {
            reset({
                ...INITIAL_STATE,
                entity: viewEntity?.entity || null,
                account: viewEntity?.entity?.account || null,
            });
            setPendingFile(null);
            setExistingFile(null);
            setRemovedExisting(false);
            setLogoPreview(defaultLogoSrc);
            return;
        }

        reset({
            ...INITIAL_STATE,
            ...priceListDetail,
            account: priceListDetail.account || null,
            entity: priceListDetail.entity || null,
            termsAndConditions: priceListDetail.termsAndConditions?.length
                ? priceListDetail.termsAndConditions
                : INITIAL_STATE.termsAndConditions,
        });

        setExistingFile(priceListDetail.excelAttachment || null);
        setPendingFile(null);
        setRemovedExisting(false);
        setLogoPreview(defaultLogoSrc);

        return () => { isMounted.current = false; };
    }, [priceListDetail, reset, viewEntity]);

    useEffect(() => {
        if ((isAccountAdmin || isSystemAdmin) && _watchAccount?._id && isMounted.current) {
            fetchEntities({ account: _watchAccount._id });
        }
    }, [_watchAccount?._id, fetchEntities, isAccountAdmin, isSystemAdmin]);

    // ── Logo ──
    const handleLogoSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setValue("logoFile", file);
        setLogoPreview(URL.createObjectURL(file));
    };
    const handleLogoReset = () => {
        setValue("logoFile", null);
        setLogoPreview(defaultLogoSrc);
        if (logoInputRef.current) logoInputRef.current.value = "";
    };

    // ── Excel file ──
    const handleExcelSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setPendingFile(file);
        setRemovedExisting(true); // pending replaces whatever was there
        if (excelInputRef.current) excelInputRef.current.value = "";
    };

    const handleRemovePending = () => {
        setPendingFile(null);
        if (excelInputRef.current) excelInputRef.current.value = "";
    };

    const handleRemoveExisting = () => {
        setExistingFile(null);
        setRemovedExisting(true);
    };

    // ── Terms ──
    const addTerm = () =>
        setValue("termsAndConditions", [...termsAndConditions, ""]);
    const removeTerm = (idx) =>
        setValue("termsAndConditions", termsAndConditions.filter((_, i) => i !== idx));
    const updateTerm = (idx, value) =>
        setValue("termsAndConditions", termsAndConditions.map((t, i) => (i === idx ? value : t)));

    const handleOnSubmit = async (data) => {
        if (!pendingFile && !existingFile) {
            toast.error("Please upload an Excel file.");
            return;
        }

        try {
            let excelAttachment = null;

            if (pendingFile) {
                // 1. Parse the imported file to extract the grid
                const { brands, locations, entries } = await parseVendorQuotationExcel(pendingFile);

                // 2. Generate a new Excel with logo + form metadata + parsed grid
                const wb = await generateVendorQuotationExcel({
                    ...data,
                    brands,
                    locations,
                    entries,
                });

                // 3. Write to buffer and upload
                const buffer = await wb.xlsx.writeBuffer();
                const fileName = `${data.title || "price-list"}.xlsx`;
                const blob = new Blob([buffer], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                });
                const generatedFile = new File([blob], fileName, { type: blob.type });

                const { uploadedFiles } = await handleUploadFiles([generatedFile], {
                    folderName: "PriceList",
                    entity: viewEntity?.entity?._id,
                });
                excelAttachment = uploadedFiles[0];

            } else if (!removedExisting && existingFile) {
                // No new file selected, keep the existing attachment
                excelAttachment = existingFile;
            }
            // removedExisting && !pendingFile → excelAttachment = null → clears in DB

            onSubmit({
                ...data,
                logoFile: undefined,
                account: data?.account?._id,
                entity: data?.entity?._id,
                excelAttachment,
                isImported: true,
            });

        } catch (err) {
            console.error("Excel parse/generate error:", err);
            toast.error(err?.message ?? "Failed to process Excel file.");
        }
    };

    const isSubmitting = isBusy || uploadingFiles;
    const hasExisting = existingFile && !removedExisting;

    // ── Render ──
    return (
        <AppForm loading={isSubmitting}>
            <AppForm.Content>
                <Grid container spacing={3}>

                    {/* ── Account & Entity ── */}
                    {(isAccountAdmin || isSystemAdmin) && (
                        <>
                            <Grid item xs={12} md={6}>
                                <Controller
                                    name="account" control={control}
                                    rules={{ required: "Please select an account." }}
                                    render={({ field }) => (
                                        <BaseAutocomplete
                                            {...field} fullWidth required label="Account"
                                            value={field.value ?? null}
                                            onChange={(_, data) => { field.onChange(data); setValue("entity", null); }}
                                            getOptionLabel={(o) => o?.name ?? ""}
                                            isOptionEqualToValue={(o, v) => o?._id === v?._id}
                                            options={accounts || []} loading={loadingAccounts}
                                            loadingText="Loading Account..." disabled={isAccountAdmin}
                                            helperText={errors?.account?.message} error={!!errors?.account}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Controller
                                    name="entity" control={control}
                                    rules={{ required: "Please select an entity." }}
                                    render={({ field: { value, onChange } }) => (
                                        <BaseAutocomplete
                                            fullWidth required label="Entity"
                                            value={value ?? null}
                                            onChange={(_, data) => onChange(data)}
                                            getOptionLabel={(o) => o?.name ?? ""}
                                            isOptionEqualToValue={(o, v) => o?._id === v?._id}
                                            options={entities || []} loading={loadingEntities}
                                            loadingText="Loading Entities..."
                                            helperText={errors?.entity?.message} error={!!errors?.entity}
                                        />
                                    )}
                                />
                            </Grid>
                        </>
                    )}

                    {/* ── Title ── */}
                    <Grid item xs={12}>
                        <Controller
                            name="title" control={control}
                            rules={{ required: "Title is required." }}
                            render={({ field }) => (
                                <TextField
                                    {...field} fullWidth required size="small" label="Title"
                                    error={!!errors.title} helperText={errors.title?.message}
                                />
                            )}
                        />
                    </Grid>

                    {/* ── Excel Header ── */}
                    <SectionHeading>Excel Header</SectionHeading>

                    <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" mb={1}>Logo</Typography>
                        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                            <Box
                                component="img" src={logoPreview} alt="Logo Preview"
                                sx={{
                                    height: 64, maxWidth: 180, objectFit: "contain",
                                    border: "1px solid", borderColor: "divider",
                                    borderRadius: 1, p: 0.5, background: "#fff",
                                }}
                            />
                            <Box display="flex" gap={1}>
                                <input type="file" accept="image/*" ref={logoInputRef}
                                    style={{ display: "none" }} onChange={handleLogoSelect} />
                                <Button size="small" variant="outlined" startIcon={<UploadFile />}
                                    onClick={() => logoInputRef.current?.click()}>
                                    Change Logo
                                </Button>
                                <Button size="small" color="error" onClick={handleLogoReset}>
                                    Reset to Default
                                </Button>
                            </Box>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Controller name="headerCompanyName" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="Company Name" />
                            )} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller name="headerFormerName" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="Formerly Known As" />
                            )} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller name="headerRegnNo" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="Regd No." />
                            )} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller name="headerPanNo" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="PAN No." />
                            )} />
                    </Grid>

                    {/* ── Quotation Details ── */}
                    <SectionHeading>Quotation Details</SectionHeading>

                    <Grid item xs={12} md={4}>
                        <Controller
                            name="date" control={control}
                            rules={{ required: "Date is required." }}
                            render={({ field }) => (
                                <TextField
                                    {...field} fullWidth required type="date" size="small" label="Date"
                                    InputLabelProps={{ shrink: true }}
                                    error={!!errors.date} helperText={errors.date?.message}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <Controller name="subject" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="Subject" />
                            )} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller name="toName" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="To — Name" />
                            )} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller name="toAddress" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="To — Address" />
                            )} />
                    </Grid>

                    {/* ── Excel File Import ── */}
                    <SectionHeading>Price List Excel</SectionHeading>

                    <Grid item xs={12}>
                        <input
                            type="file"
                            accept=".xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            ref={excelInputRef}
                            style={{ display: "none" }}
                            onChange={handleExcelSelect}
                        />

                        {/* Existing saved file (edit mode) */}
                        {hasExisting && (
                            <Paper
                                variant="outlined"
                                sx={{
                                    display: "flex", alignItems: "center", gap: 1.5,
                                    px: 2, py: 1.5, mb: 1.5, borderRadius: 2,
                                }}
                            >
                                <InsertDriveFile color="success" />
                                <Box flex={1} overflow="hidden">
                                    <Typography variant="body2" fontWeight={600} noWrap>
                                        {existingFile?.fileName || existingFile?.originalName || "Saved Excel File"}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Currently saved — upload a new file to replace
                                    </Typography>
                                </Box>
                                <IconButton size="small" color="error" onClick={handleRemoveExisting}>
                                    <Delete fontSize="small" />
                                </IconButton>
                            </Paper>
                        )}

                        {/* Pending new file */}
                        {pendingFile && (
                            <Paper
                                variant="outlined"
                                sx={{
                                    display: "flex", alignItems: "center", gap: 1.5,
                                    px: 2, py: 1.5, mb: 1.5, borderRadius: 2,
                                    borderColor: "primary.main",
                                }}
                            >
                                <InsertDriveFile color="primary" />
                                <Box flex={1} overflow="hidden">
                                    <Typography variant="body2" fontWeight={600} noWrap>
                                        {pendingFile.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {(pendingFile.size / 1024).toFixed(1)} KB — ready to upload
                                    </Typography>
                                </Box>
                                <IconButton size="small" color="error" onClick={handleRemovePending}>
                                    <Delete fontSize="small" />
                                </IconButton>
                            </Paper>
                        )}

                        {/* Upload button — hidden when pending is selected */}
                        {!pendingFile && (
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<UploadFile />}
                                onClick={() => excelInputRef.current?.click()}
                            >
                                {hasExisting ? "Replace Excel File" : "Upload Excel File"}
                            </Button>
                        )}
                    </Grid>

                    {/* ── Terms & Conditions ── */}
                    <SectionHeading>Terms &amp; Conditions</SectionHeading>

                    {termsAndConditions?.map((term, idx) => (
                        <Grid item xs={12} key={idx}>
                            <Box display="flex" alignItems="flex-start" gap={1}>
                                <Typography variant="body2" sx={{ mt: 1, minWidth: 22, fontWeight: 600 }}>
                                    {idx + 1}.
                                </Typography>
                                <TextField
                                    fullWidth size="small" multiline label={`Term ${idx + 1}`}
                                    value={term} onChange={(e) => updateTerm(idx, e.target.value)}
                                />
                                <IconButton
                                    size="small" color="error" sx={{ mt: 0.5 }}
                                    onClick={() => removeTerm(idx)}
                                    disabled={termsAndConditions.length === 1}
                                >
                                    <Delete fontSize="small" />
                                </IconButton>
                            </Box>
                        </Grid>
                    ))}
                    <Grid item xs={12}>
                        <Button size="small" startIcon={<Add />} onClick={addTerm}>
                            Add Term
                        </Button>
                    </Grid>

                    {/* ── Closing & Signatory ── */}
                    <SectionHeading>Closing &amp; Signatory</SectionHeading>

                    <Grid item xs={12}>
                        <Controller name="closingNote" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="Closing Note" />
                            )} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller name="thankYouNote" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="Thank You Text" />
                            )} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller name="regards" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="Regards Text" />
                            )} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller name="signatoryName" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="Signatory Name" />
                            )} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller name="signatoryDesignation" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="Designation" />
                            )} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller name="signatoryCompany" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="Company" />
                            )} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller name="signatoryAddress" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="Address" />
                            )} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller name="signatoryPhone" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="Phone" />
                            )} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller name="signatoryEmail" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="Email" />
                            )} />
                    </Grid>

                    {/* ── Excel Footer ── */}
                    <SectionHeading>Excel Footer</SectionHeading>

                    <Grid item xs={12} md={6}>
                        <Controller name="footerLeft" control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field} fullWidth size="small" multiline rows={2}
                                    label="Footer Left"
                                    helperText={`Use \\n for a new line`}
                                />
                            )} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller name="footerCenter" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="Footer Center (Phone)" />
                            )} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller name="footerRightWeb" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="Footer Right — Website" />
                            )} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller name="footerRightEmail" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="Footer Right — Email" />
                            )} />
                    </Grid>

                    {/* ── Active toggle (edit only) ── */}
                    {priceListDetail && (
                        <Grid item xs={6}>
                            <Controller name="isActive" control={control}
                                render={({ field }) => <AppSwitch {...field} label="Is Active?" />} />
                        </Grid>
                    )}
                </Grid>
            </AppForm.Content>

            <AppForm.Actions>
                <Button onClick={() => navigate(-1)} disabled={isSubmitting}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit(handleOnSubmit)}
                    disabled={isSubmitting}
                >
                    {isBusy
                        ? "Saving…"
                        : priceListDetail
                            ? "Update Vendor Quotation Excel"
                            : "Add Vendor Quotation Excel"}
                </Button>
            </AppForm.Actions>
        </AppForm>
    );
};

export default VendorQuotationExcelImportForm;